import React, { Component, useState, useRef } from "react";
import { json, useNavigate } from "react-router-dom";
import Icon from "components/Icon";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import { read, utils } from "xlsx";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import useReports from "services/ReportService";
import { useOktaAuth } from "@okta/okta-react";
// import { FileUpload } from 'primereact/fileupload';
// import "primereact/resources/themes/lara-light-cyan/theme.css";
import templateFile from "../../assets/architect_template.xlsx";
import { ProgressBar } from "primereact/progressbar";

import Button from "../Button";
import DownloadButton from "../DownloadButton";

registerPlugin(FilePondPluginFileValidateType);

export default function Form({ clients, closeModal }) {
  const [uploadedFile, setUploadedFile] = useState();
  const [processedFile, setProcessedFile] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const createReportDisabled =
    uploadedFile === undefined || errorMessage !== "";

  const version = 5;

  const testing = false;

  const sheets = ["Performance", "CRO", "DS", "KW Trends"];

  const cleanValues = (values) => {
    return values.map((value) => {
      if (typeof value === "string") {
        return value.trim().replace(/[™©]/g, "");
      }
      return value;
    });
  };

  var DecPrec = (function () {
    if (Number.EPSILON === undefined) {
      Number.EPSILON = Math.pow(2, -52);
    }
    if (Math.sign === undefined) {
      Math.sign = function (x) {
        return (x > 0) - (x < 0) || +x;
      };
    }
    return {
      // Decimal round (half away from zero)
      round: function (num, decimalPlaces) {
        var p = Math.pow(10, decimalPlaces || 0);
        var n = num * p * (1 + Number.EPSILON);
        return Math.round(n) / p;
      },
      // Decimal ceil
      ceil: function (num, decimalPlaces) {
        var p = Math.pow(10, decimalPlaces || 0);
        var n = num * p * (1 - Math.sign(num) * Number.EPSILON);
        return Math.ceil(n) / p;
      },
      // Decimal floor
      floor: function (num, decimalPlaces) {
        var p = Math.pow(10, decimalPlaces || 0);
        var n = num * p * (1 + Math.sign(num) * Number.EPSILON);
        return Math.floor(n) / p;
      },
      // Decimal trunc
      trunc: function (num, decimalPlaces) {
        return (num < 0 ? this.ceil : this.floor)(num, decimalPlaces);
      },
      // Format using fixed-point notation
      toFixed: function (num, decimalPlaces) {
        return this.round(num, decimalPlaces).toFixed(decimalPlaces);
      },
    };
  })();

  const handleFileLoad = (error, files) => {
    if (error) {
      setErrorMessage(
        "Incorrect file type, please upload the filled out template xlsx file"
      );
    } else if (files) {
      try {
        const file = files.file;
        setUploadedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = read(data, { type: "array" });
          if (
            !workbook.SheetNames.includes("info") ||
            utils.sheet_to_json(workbook.Sheets["info"])[0].version !== version
          ) {
            setErrorMessage(
              "Outdated template, please download and populate the latest version."
            );
            return;
          } else if (
            !sheets.some((item) => workbook.SheetNames.includes(item))
          ) {
            setErrorMessage("No relevant sheets found.");
            return;
          } else {
            setErrorMessage("");
            let performance = workbook.SheetNames.includes("Performance")
              ? utils.sheet_to_json(workbook.Sheets["Performance"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let stat = workbook.SheetNames.includes("DS")
              ? utils.sheet_to_json(workbook.Sheets["DS"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let keywordInsights = workbook.SheetNames.includes(
              "Keyword Insights"
            )
              ? utils.sheet_to_json(workbook.Sheets["Keyword Insights"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let cro = workbook.SheetNames.includes("CRO")
              ? utils.sheet_to_json(workbook.Sheets["CRO"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let ctrAnalysis = workbook.SheetNames.includes("CTR Analysis")
              ? utils.sheet_to_json(workbook.Sheets["CTR Analysis"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let serpReport = workbook.SheetNames.includes("SERP Report")
              ? utils.sheet_to_json(workbook.Sheets["SERP Report"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let serpIndex = workbook.SheetNames.includes("SERP Index")
              ? utils.sheet_to_json(workbook.Sheets["SERP Index"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let kwTrends = workbook.SheetNames.includes("KW Trends")
              ? utils.sheet_to_json(workbook.Sheets["KW Trends"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })
              : [];
            let kwHeaders = workbook.SheetNames.includes("KW Trends")
              ? utils.sheet_to_json(workbook.Sheets["KW Trends"], {
                  header: 1,
                  defval: null,
                  raw: true,
                })[0]
              : [];
            performance = cleanValues(performance);
            stat = cleanValues(stat);
            keywordInsights = cleanValues(keywordInsights);
            cro = cleanValues(cro);
            ctrAnalysis = cleanValues(ctrAnalysis);
            serpReport = cleanValues(serpReport);
            serpIndex = cleanValues(serpIndex);
            kwTrends = cleanValues(kwTrends);
            setProcessedFile({
              performance: performance,
              stat: stat,
              keywordInsights: keywordInsights,
              cro: cro,
              ctrAnalysis: ctrAnalysis,
              serpReport: serpReport,
              serpIndex: serpIndex,
              kwTrends: kwTrends,
              kwHeaders: kwHeaders,
            });
            // console.log(processedFile);
            // const byteSize = new TextEncoder().encode(processedFile).length;

            // console.log(byteSize);

          }
        };
        reader.readAsArrayBuffer(file);
      } catch {
        setUploadedFile(undefined);
        setErrorMessage("Something went wrong.");
      }
    } else {
      setUploadedFile(undefined);
      setErrorMessage("");
    }
  };

  const [title, setTitle] = useState("");
  const [client, setClient] = useState("");
  const [currency, setCurrency] = useState("");

  const [comp1, setComp1] = useState("");
  const [comp2, setComp2] = useState("");
  const [comp3, setComp3] = useState("");
  const [comp4, setComp4] = useState("");

  const currencies = [
    { value: "EUR (€)" },
    { value: "GBP (£)" },
    { value: "USD ($)" },
    { value: "CHF (₣)" },
  ];
  const industries = [{ value: "Fashion/Retail" }, { value: "Plumbing" }];

  const clientObj = () => {
    if (clients.length > 0) {
      return clients
        .map((item) => ({ value: item }))
        .sort((a, b) => a.value.localeCompare(b.value));
    } else {
      return [];
    }
  };

  const nextDisabled = title === "" || client === "" || currency === "";

  const [loading, setLoading] = useState(false);

  const [modalStep, setModalStep] = useState(1);
  const incModalStep = () => setModalStep((modalStep) => modalStep + 1);
  const decModalStep = () => setModalStep((modalStep) => modalStep - 1);

  const { newReport } = useReports();
  const [reportData, setReportData] = useState();

  const { authState } = useOktaAuth();

  const history = useNavigate();

  const createReport = async () => {
    incModalStep();

    let competitors = [comp1, comp2, comp3, comp4].filter(
      (name) => name !== ""
    );

    const info = {
      title: title,
      currency: currency,
      client: client,
      competitors: competitors,
    };

    const transformedData = {
      ...info,
      file: processedFile,
    };

    // console.log(transformedData);

    let report = await newReport(transformedData);
    if (report.status === 200) {
      history(`/report/${report.data.id}/report-overview`);
    } else {
      decModalStep();
      setErrorMessage(report.response.data);
    }
  };

  const stepTitle = ["Create New Report", "Competitors", "Data Upload"];

  return (
    <>
      {modalStep < 4 && (
        <>
          <div className="modal-main">
            <div className="modal-title">
              <div className="pagination">
                Step <span className="purple">{modalStep}</span> of 3
              </div>
              <h2>{stepTitle[modalStep - 1]}</h2>
            </div>
            <div className="modal-main-inner">
              <div className="form">
                <form className="new-report">
                  {modalStep === 1 && (
                    <>
                      <div className="top-level-metrics">
                        <div className="dropdowns nowrap">
                          <InputText
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            max={100}
                            maxLength={100}
                            placeholder="Enter Report Title"
                            className="input-title"
                          />
                        </div>
                        <div className="dropdowns">
                          <Dropdown
                            value={client}
                            onChange={(e) => setClient(e.value)}
                            options={clientObj()}
                            optionLabel="value"
                            placeholder="Select Client"
                            className="input-client"
                            filter
                          />
                          <Dropdown
                            value={currency}
                            options={currencies}
                            onChange={(e) => setCurrency(e.value)}
                            optionLabel="value"
                            placeholder="Select Currency"
                            className="input-currency"
                          />
                        </div>
                      </div>
                      <DownloadButton
                        file={templateFile}
                        style={{ margin: "auto" }}
                        text={"Download Data Template"}
                      />
                    </>
                  )}
                  {modalStep === 2 && (
                    <>
                      <div className="top-level-metrics">
                        <div className="dropdowns">
                          <InputText
                            value={comp1}
                            onChange={(e) => setComp1(e.target.value)}
                            max={50}
                            maxLength={50}
                            placeholder="Comptetitor 1 Name"
                            className="comptetitor-input"
                          />
                          <InputText
                            value={comp2}
                            onChange={(e) => setComp2(e.target.value)}
                            max={50}
                            maxLength={50}
                            placeholder="Comptetitor 2 Name"
                            className="comptetitor-input"
                          />
                        </div>
                        <div className="dropdowns">
                          <InputText
                            value={comp3}
                            onChange={(e) => setComp3(e.target.value)}
                            max={50}
                            maxLength={50}
                            placeholder="Comptetitor 3 Name"
                            className="comptetitor-input"
                          />
                          <InputText
                            value={comp4}
                            onChange={(e) => setComp4(e.target.value)}
                            max={50}
                            maxLength={50}
                            placeholder="Comptetitor 4 Name"
                            className="comptetitor-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {modalStep === 3 && (
                    <>
                      <div className="report-info">
                        <div className="report-info__item">
                          <div className="report-info__item__label">
                            Report Name
                          </div>
                          <div className="report-info__item__value">
                            {title}
                          </div>
                        </div>
                        <div className="report-info__item">
                          <div className="report-info__item__label">Client</div>
                          <div className="report-info__item__value">
                            {client}
                          </div>
                        </div>
                        <div className="report-info__item">
                          <div className="report-info__item__label">
                            Currency
                          </div>
                          <div className="report-info__item__value">
                            {currency}
                          </div>
                        </div>
                      </div>
                      <div className="data-upload">
                        <p className="bold">
                          Upload your populated data template here to generate
                          report.
                        </p>
                        <FilePond
                          credits={false}
                          acceptedFileTypes={[
                            "application/vnd.ms-excel",
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                            "application/vnd.ms-excel.sheet.macroEnabled.12",
                          ]}
                          labelIdle='Drag & Drop your file here or <br> <span class="filepond--label-action">click here to browse</span>'
                          files={uploadedFile ? [uploadedFile] : []}
                          onaddfile={handleFileLoad}
                        />
                        {errorMessage && (
                          <p style={{ color: "red", marginTop: 0 }}>
                            {errorMessage}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <div className="close-modal">
              <Button
                className={"cancel-button border"}
                onClick={closeModal}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
            {modalStep === 1 && (
              <Button onClick={incModalStep} disabled={nextDisabled}>
                Next
              </Button>
            )}
            {modalStep === 2 && (
              <div className="group">
                <div className="back-step">
                  <Button onClick={decModalStep}>Back</Button>
                </div>
                <div className="create-report">
                  <Button onClick={incModalStep}>Next</Button>
                </div>
              </div>
            )}
            {modalStep === 3 && (
              <div className="group">
                <div className="back-step">
                  <Button onClick={decModalStep}>Back</Button>
                </div>
                <div className="create-report">
                  <Button
                    disabled={createReportDisabled}
                    onClick={createReport}
                  >
                    Create Report
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {modalStep === 4 && (
        <div className="modal-main">
          <div className="modal-main-inner">
            <ProgressBar
              mode="indeterminate"
              style={{ height: "6px" }}
            ></ProgressBar>
          </div>
        </div>
      )}
    </>
  );
}
